"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.PhoneFieldLogic = void 0;
const libphonenumber_js_1 = require("libphonenumber-js");
const types_1 = require("@smartsuite/types");
const field_logic_1 = require("../../field-type-registry/field-logic");
const texts_1 = require("../../texts");
const intl_1 = require("../../locale/intl");
const comparisons_1 = require("../../comparisons");
const phone_fieldtype_1 = require("../../safe-field-value/text-fields/phone-field/phone.fieldtype");
const clipboard_1 = require("../../clipboard");
const validation_1 = require("../../validation");
const isString_1 = __importDefault(require("lodash-es/isString"));
const phone_number_1 = require("../../formatters/phone-number");
const phone_1 = require("../../utils/phone");
const filter_1 = require("../../utils/filter");
const fields_1 = require("../../utils/fields");
const differenceBy_1 = __importDefault(require("lodash-es/differenceBy"));
const migration_1 = require("../../utils/migration");
const text_1 = require("../../utils/text");
const bulk_update_1 = require("../../utils/bulk-update");
const phonesDelimiterRegex = /[,;|]+/;
class PhoneFieldLogic extends field_logic_1.BaseFieldTypeLogic {
    constructor() {
        super(...arguments);
        this.name = "phonefield";
        this.label = (0, intl_1.$t)(texts_1.phoneFieldText.phone);
        this.description = (0, intl_1.$t)(texts_1.phoneFieldText.field_description);
        this.icon = "phone";
        this.iconColor = "#FFB938";
        this.placeholder = (0, intl_1.$t)(texts_1.phoneFieldText.enter_phone_here);
        this.searchSynonyms = [
            (0, intl_1.$t)(texts_1.phoneFieldText.telephone),
            (0, intl_1.$t)(texts_1.phoneFieldText.cell),
            (0, intl_1.$t)(texts_1.phoneFieldText.mobile_lower),
        ];
        // @ts-expect-error: Need to resolve
        this.comparisons = {
            is: comparisons_1.isListExact,
            is_not: comparisons_1.isNotListExact,
            is_empty: comparisons_1.isEmptyList,
            is_not_empty: comparisons_1.isNotEmptyList,
            contains: comparisons_1.containsList,
            not_contains: comparisons_1.notContainsList,
        };
        this.fieldValueGetter = new phone_fieldtype_1.PhoneFieldValueGetter();
        this.prepareSearchTermRegExp = /[\s()-]/g;
        this.clipboardPasteRules = {
            phonefield: clipboard_1.clipboardPhoneSelf,
            textfield: clipboard_1.clipboardTextToPhone,
            recordtitlefield: clipboard_1.clipboardTextToPhone,
            textareafield: clipboard_1.clipboardTextToPhone,
            richtextareafield: clipboard_1.clipboardTextToPhone,
            linkedrecordfield: clipboard_1.clipboardJsonToPhone,
            tagsfield: clipboard_1.clipboardTagsToPhone,
            unknownfield: clipboard_1.clipboardTextToPhone,
        };
    }
    get validators() {
        return { requiredPhoneList: validation_1.requiredPhoneList, phoneField: validation_1.phoneField };
    }
    valueToString(fieldValue, field) {
        if ((0, isString_1.default)(fieldValue)) {
            return fieldValue;
        }
        const showPhoneType = field.params.display_format === "number_flag_type";
        return fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.map((item) => (0, phone_number_1.formatPhoneNumber)(item, showPhoneType)).join(", ");
    }
    sortKey(fieldValue, _field) {
        return fieldValue.length > 0
            ? fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.map((item) => item.sys_root).join(", ")
            : "";
    }
    processFilterValue(filterValue, comparison) {
        const filterComparisons = ["contains", "not_contains", "is", "is_not"];
        if (filterComparisons.includes(comparison)) {
            return (0, text_1.processMultipleFilterValue)(filterValue, phonesDelimiterRegex).map(phone_1.sanitizePhoneNumber);
        }
        return filterValue;
    }
    filterToRecordValue(filterValue, comparison, field) {
        var _a, _b;
        if (comparison !== "is" || !filterValue) {
            throw new types_1.FilterToRecordValueConversionError(field, filterValue, comparison);
        }
        const values = (0, text_1.processMultipleFilterValue)(filterValue, phonesDelimiterRegex).map(phone_1.sanitizePhoneNumber);
        const defaultCountry = (_b = (_a = field.params.default_country) === null || _a === void 0 ? void 0 : _a.toUpperCase()) !== null && _b !== void 0 ? _b : types_1.DEFAULT_COUNTRY;
        return values.map((value) => {
            const phoneNumber = (0, libphonenumber_js_1.parsePhoneNumberFromString)((0, phone_1.sanitizePhoneNumber)(filterValue), defaultCountry);
            if (!(phoneNumber === null || phoneNumber === void 0 ? void 0 : phoneNumber.isValid())) {
                throw new types_1.FilterToRecordValueConversionError(field, filterValue, comparison);
            }
            return (0, phone_1.initPhoneItem)(phoneNumber, value);
        });
    }
    getEmptyValue() {
        return [];
    }
    getInitialFieldValue(startValue) {
        return startValue ? [{ phone_number: startValue }] : this.getEmptyValue();
    }
    getGroupValues(fieldValue) {
        if (!Array.isArray(fieldValue) || !fieldValue.length) {
            return [[]];
        }
        return fieldValue
            .filter(filter_1.filterUniqueValues)
            .map((valueItem) => [valueItem]);
    }
    processFieldValue(record, slug, comparison, field) {
        const fieldValue = super.processFieldValue(record, slug, comparison, field);
        const format = (item) => {
            return (0, phone_number_1.getNumber)(item, "E.164").replace(/\D+/g, "");
        };
        return fieldValue.map((item) => item.sys_root ? item.sys_root : format(item));
    }
    filterValueToString(value, field) {
        if (typeof value === "string")
            return value;
        return this.valueToString(value, field);
    }
    getInlineEditRules(field) {
        const isSingle = field.params.entries_allowed === "single";
        const baseInlineEditRules = super.getInlineEditRules(field);
        const switchToInputControl = isSingle
            ? "OnSelectedCellClick"
            : "OnCellSelect";
        const cellPresenceEventMode = isSingle ? "OnEdit" : "OnModalOpen";
        return {
            ...baseInlineEditRules,
            closeWhenClickOutside: isSingle,
            deleteValueOnFirstKeypress: isSingle,
            switchToInputControl,
            enableCopyEventInEditMode: true,
            enablePasteEventInEditMode: true,
            editorWidth: "100%",
            rollbackOnPressEscapeKey: true,
            saveKeys: isSingle ? baseInlineEditRules.saveKeys : types_1.navigationKeys,
            inputControlEditMode: "OnShow",
            cellPresenceEventMode,
            needUseDefaultValue: () => false,
            spaceKeyOpensRecordModal: (isEditMode) => !isEditMode || !isSingle,
        };
    }
    formatActivityRecordChangeValues(previousValue, currentValue, _extraData, field) {
        if (this.isValueEmpty(previousValue, field) ||
            this.isValueEmpty(currentValue, field)) {
            return { previousValue, currentValue };
        }
        const removedValues = (0, differenceBy_1.default)(previousValue, currentValue, "sys_root");
        const addedValues = (0, differenceBy_1.default)(currentValue, previousValue, "sys_root");
        return { previousValue: removedValues, currentValue: addedValues };
    }
    replaceValue(field, record, sourceValue = [], targetValue = [], _userPreferences) {
        return (0, fields_1.replaceValueInList)(field, record, sourceValue, targetValue);
    }
    getLinkedRecordFieldSmartWidth(_field) {
        return {
            defaultWidht: 170,
            minWidth: 90,
        };
    }
    migrateField(fieldTo, fieldFrom) {
        const migratedField = super.migrateField(fieldTo, fieldFrom);
        const format = fieldTo.params.display_format;
        return (0, migration_1.migrateDisplayFormat)(migratedField, types_1.phoneDisplayFormats, format);
    }
    prepareSearchTerm(searchTerm) {
        return super
            .prepareSearchTerm(searchTerm)
            .replace(this.prepareSearchTermRegExp, "");
    }
    searchKey(fieldValue, field, userPreferences, record) {
        return this.valueToString(fieldValue, field)
            .toLowerCase()
            .replace(this.prepareSearchTermRegExp, "");
    }
    /**
     * Applies a bulk update to a field value.
     */
    applyBulkUpdate(field, value, update) {
        if (field.params.entries_allowed === "single") {
            return super.applyBulkUpdate(field, value, update);
        }
        return (0, bulk_update_1.applyBulkUpdateToDictsArray)(value, update, "phone_number");
    }
}
exports.PhoneFieldLogic = PhoneFieldLogic;
